import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    TextField,
    Autocomplete,
    CircularProgress,
    Box,
    Typography,
    Stack,
    Paper,
    Chip,
    InputAdornment,
    IconButton,
    Tooltip,
} from "@mui/material";

import {
    School as SchoolIcon,
    Close as CloseIcon,
    PersonOutline,
    CalendarMonth,
    Assessment,
    SaveOutlined,
    EditOutlined,
    LockOutlined,
    VerifiedOutlined,
    InfoOutlined,
} from "@mui/icons-material";

import api from "../../api/api";
import permissionaudio from "./audio/cantuploadpermission.mp3";

const COLORS = {
    navy: "#062A4B",
    deepBlue: "#063A6B",
    gold: "#F6C944",
    orange: "#C07500",
    background: "#F3F6FB",
    white: "#FFFFFF",
    text: "#243447",
    muted: "#718096",
    border: "#E2E8F0",
    success: "#15803D",
    danger: "#DC2626",
};

const SCORE_FIELDS = [
    {
        key: "quizScore",
        label: "Quiz Score",
        description: "Quiz assessment",
        required: true,
    },
    {
        key: "assignmentScore",
        label: "Assignment Score",
        description: "Written assignments",
        required: true,
    },
    {
        key: "participationScore",
        label: "Participation Score",
        description: "Class participation",
        required: true,
    },
    {
        key: "testScore",
        label: "Test Score",
        description: "Test assessment",
        required: true,
    },
    {
        key: "scaleGrade",
        label: "Scale Grade",
        description: "Optional grading scale value",
        required: false,
    },
];

const EMPTY_SCORES = {
    quizScore: "",
    assignmentScore: "",
    participationScore: "",
    testScore: "",
    scaleGrade: "",
};

const AddAndUpDateGrades = ({
    token,
    userType,
    gradeDistributions,
    gradeClasses,
    subjects,
    periods,
    selectedYear,

    page,
    rowsPerPage,
    searchTerm,

    fetchGradeDistributions,

    setAlertMessage,
    setAlertSeverity,
    setSnackbarOpen,

    editGradeId,
    setEditGradeId,
}) => {
    // =========================================
    // FORM STATES
    // =========================================
    const [selectedGradeClass, setSelectedGradeClass] = useState("");
    const [selectedStudent, setSelectedStudent] = useState("");
    const [selectedSubject, setSelectedSubject] = useState("");
    const [selectedPeriod, setSelectedPeriod] = useState("");

    const [filteredStudents, setFilteredStudents] = useState([]);
    const [scores, setScores] = useState({ ...EMPTY_SCORES });

    const [allowTeacherUpdate, setAllowTeacherUpdate] = useState(false);

    const [editingId, setEditingId] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    const [loadingStudents, setLoadingStudents] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // =========================================
    // FIELD STYLING
    // =========================================
    const fieldSx = {
        "& .MuiOutlinedInput-root": {
            borderRadius: "11px",
            backgroundColor: COLORS.white,
            minHeight: 50,
            transition: "all 0.2s ease",
            "& fieldset": {
                borderColor: COLORS.border,
            },
            "&:hover fieldset": {
                borderColor: COLORS.deepBlue,
            },
            "&.Mui-focused fieldset": {
                borderColor: COLORS.gold,
                borderWidth: 2,
            },
        },
        "& .MuiInputLabel-root.Mui-focused": {
            color: COLORS.deepBlue,
        },
        "& .MuiFormHelperText-root": {
            marginLeft: "2px",
            fontSize: "10px",
        },
    };

    // =========================================
    // PERMISSION AUDIO
    // =========================================
    const playPermissionAudio = () => {
        try {
            const audio = new Audio(permissionaudio);

            audio.volume = 1;
            audio.currentTime = 0;

            const playPromise = audio.play();

            if (playPromise && typeof playPromise.catch === "function") {
                playPromise.catch((error) => {
                    console.error(
                        "Could not play permission-denied audio:",
                        error
                    );
                });
            }
        } catch (error) {
            console.error(
                "Unable to initialize permission-denied audio:",
                error
            );
        }
    };

    // =========================================
    // PROFESSIONAL SWEETALERT2 STYLING
    // =========================================
    useEffect(() => {
        const styleId = "grade-form-alert-styles";

        if (document.getElementById(styleId)) {
            return undefined;
        }

        const style = document.createElement("style");
        style.id = styleId;

        style.textContent = `
            .grade-form-alert {
                width: min(420px, calc(100% - 32px)) !important;
                padding: 28px 26px 24px !important;
                border: 1px solid #E2E8F0 !important;
                border-radius: 18px !important;
                font-family: "Segoe UI", Arial, sans-serif !important;
                box-shadow: 0 24px 70px rgba(6, 42, 75, .22) !important;
            }

            .grade-form-alert .swal2-icon {
                margin: 0 auto 18px !important;
            }

            .grade-form-alert-title {
                color: #062A4B !important;
                font-size: 22px !important;
                font-weight: 800 !important;
                letter-spacing: -.3px !important;
            }

            .grade-form-alert-message {
                color: #64748B !important;
                font-size: 14px !important;
                line-height: 1.7 !important;
                white-space: pre-line !important;
            }

            .grade-form-alert-button {
                min-width: 125px !important;
                padding: 12px 24px !important;
                border: none !important;
                border-radius: 9px !important;
                background: #062A4B !important;
                color: #FFFFFF !important;
                font-size: 13px !important;
                font-weight: 750 !important;
                cursor: pointer !important;
                transition: background .2s ease, transform .2s ease !important;
            }

            .grade-form-alert-button:hover {
                background: #063A6B !important;
                transform: translateY(-1px);
            }

            .grade-form-alert .swal2-success-ring {
                border-color: rgba(21, 128, 61, .25) !important;
            }

            .grade-form-alert .swal2-success-line-tip,
            .grade-form-alert .swal2-success-line-long {
                background-color: #15803D !important;
            }
        `;

        document.head.appendChild(style);

        return () => {
            style.remove();
        };
    }, []);

    // =========================================
    // CENTRALIZED SWEETALERT2 FUNCTION
    // =========================================
    const showFormAlert = async ({
        icon = "info",
        title,
        text,
        confirmButtonText = "Understood",
        timer,
        showConfirmButton = true,
    }) => {
        setDialogOpen(false);
        setSnackbarOpen(false);

        setAlertMessage(text);
        setAlertSeverity(
            icon === "success" ? "success" : "error"
        );

        // Allow the dialog's close transition to begin before Swal opens.
        await new Promise((resolve) => {
            requestAnimationFrame(() => {
                requestAnimationFrame(resolve);
            });
        });

        return Swal.fire({
            icon,
            title,
            text,
            confirmButtonText,
            confirmButtonColor: COLORS.navy,
            timer,
            showConfirmButton,
            buttonsStyling: false,
            customClass: {
                popup: "grade-form-alert",
                title: "grade-form-alert-title",
                htmlContainer: "grade-form-alert-message",
                confirmButton: "grade-form-alert-button",
            },
            allowOutsideClick: true,
            allowEscapeKey: true,
        });
    };

    // =========================================
    // BACKEND ERROR EXTRACTION
    // =========================================
    const getBackendErrorMessage = (error) => {
        const responseData = error?.response?.data;

        if (!responseData) {
            if (
                error?.message === "Network Error" ||
                !error?.response
            ) {
                return (
                    "Unable to connect to the server. " +
                    "Please check your internet connection " +
                    "or confirm that the backend server is running."
                );
            }

            return (
                error?.message ||
                "An unexpected error occurred."
            );
        }

        if (typeof responseData.error === "string") {
            return responseData.error;
        }

        if (typeof responseData.detail === "string") {
            return responseData.detail;
        }

        if (Array.isArray(responseData.detail)) {
            return responseData.detail
                .map((message) => String(message))
                .join("\n");
        }

        if (Array.isArray(responseData)) {
            return responseData
                .map((message) => String(message))
                .join("\n");
        }

        if (typeof responseData === "object") {
            const messages = [];

            Object.entries(responseData).forEach(([field, errors]) => {
                const readableField = field
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (character) =>
                        character.toUpperCase()
                    );

                if (Array.isArray(errors)) {
                    messages.push(
                        `${readableField}: ${errors
                            .map((item) =>
                                typeof item === "object"
                                    ? JSON.stringify(item)
                                    : String(item)
                            )
                            .join(", ")}`
                    );
                } else if (errors && typeof errors === "object") {
                    messages.push(
                        `${readableField}: ${JSON.stringify(errors)}`
                    );
                } else {
                    messages.push(
                        `${readableField}: ${String(errors)}`
                    );
                }
            });

            if (messages.length > 0) {
                return messages.join("\n");
            }
        }

        return "The server returned an unknown error.";
    };

    // =========================================
    // DISPLAY BACKEND ERRORS
    // =========================================
    const showBackendError = (error) => {
        console.error(
            "Grade Distribution Error:",
            error?.response?.data || error
        );

        return showFormAlert({
            icon: "error",
            title: "Unable to Save Grade",
            text: getBackendErrorMessage(error),
            confirmButtonText: "Understood",
        });
    };

    // =========================================
    // LOAD STUDENTS FOR SELECTED CLASS
    // =========================================
    useEffect(() => {
        if (!selectedGradeClass || !selectedYear?.id) {
            setFilteredStudents([]);
            setLoadingStudents(false);
            return undefined;
        }

        let cancelled = false;

        const loadStudents = async () => {
            try {
                setLoadingStudents(true);

                const response = await api.get(
                    `/students-in-class/${selectedGradeClass}/`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        params: {
                            academic_year: selectedYear.id,
                        },
                    }
                );

                if (cancelled) return;

                setFilteredStudents(
                    Array.isArray(response.data)
                        ? response.data
                        : response.data?.results || []
                );
            } catch (error) {
                if (cancelled) return;

                console.error(
                    "Error loading students:",
                    error?.response?.data || error
                );

                setFilteredStudents([]);

                await showFormAlert({
                    icon: "error",
                    title: "Unable to Load Students",
                    text: getBackendErrorMessage(error),
                });
            } finally {
                if (!cancelled) {
                    setLoadingStudents(false);
                }
            }
        };

        loadStudents();

        return () => {
            cancelled = true;
        };
    }, [selectedGradeClass, selectedYear?.id, token]);

    // =========================================
    // RESET FORM
    // =========================================
    const resetForm = () => {
        setSelectedGradeClass("");
        setSelectedStudent("");
        setSelectedSubject("");
        setSelectedPeriod("");

        setScores({ ...EMPTY_SCORES });

        setAllowTeacherUpdate(false);
        setEditingId(null);
        setEditMode(false);
        setFilteredStudents([]);
    };

    // =========================================
    // OPEN ADD / EDIT DIALOG
    // =========================================
    const handleOpenDialog = (id = null) => {
        if (submitting) return;

        if (id !== null && id !== undefined) {
            const item = (gradeDistributions || []).find(
                (grade) => String(grade.id) === String(id)
            );

            if (!item) {
                setEditGradeId(null);

                showFormAlert({
                    icon: "error",
                    title: "Grade Not Found",
                    text:
                        "The selected grade could not be found " +
                        "in the currently loaded records. Refresh " +
                        "the records and try again.",
                });

                return;
            }

            setEditingId(item.id);
            setEditMode(true);

            setSelectedGradeClass(item.grade_class ?? "");
            setSelectedStudent(item.student ?? "");
            setSelectedSubject(item.subject ?? "");
            setSelectedPeriod(item.period ?? "");

            setScores({
                quizScore: item.quiz_score ?? "",
                assignmentScore: item.assignment_score ?? "",
                participationScore: item.participation_score ?? "",
                testScore: item.test_score ?? "",
                scaleGrade: item.scale_grade ?? "",
            });

            setAllowTeacherUpdate(
                Boolean(item.allow_teacher_update)
            );
        } else {
            resetForm();
        }

        setDialogOpen(true);
    };

    // =========================================
    // AUTOMATICALLY OPEN EDIT DIALOG
    // =========================================
    useEffect(() => {
        if (
            editGradeId !== null &&
            editGradeId !== undefined
        ) {
            handleOpenDialog(editGradeId);
        }
    }, [editGradeId]);

    // =========================================
    // CLOSE DIALOG
    // =========================================
    const handleCloseDialog = () => {
        if (submitting) return;

        setDialogOpen(false);
        setEditGradeId(null);
    };

    // =========================================
    // SCORE INPUT HANDLER
    // =========================================
    const handleScoreChange = (field, value) => {
        setScores((previous) => ({
            ...previous,
            [field]: value,
        }));
    };

    // =========================================
    // VALIDATE SCORES
    // SCALE GRADE IS OPTIONAL
    // =========================================
    const validateScores = () => {
        for (const field of SCORE_FIELDS) {
            const value = scores[field.key];

            const isEmpty =
                value === "" ||
                value === null ||
                value === undefined ||
                (
                    typeof value === "string" &&
                    value.trim() === ""
                );

            if (!field.required && isEmpty) {
                continue;
            }

            if (isEmpty) {
                return (
                    `${field.label} is required. ` +
                    "Enter a score, or enter 0 if the student scored zero."
                );
            }

            if (!Number.isFinite(Number(value))) {
                return `${field.label} must be a valid number.`;
            }

            if (Number(value) < 0) {
                return `${field.label} cannot be negative.`;
            }
        }

        return null;
    };

    // =========================================
    // BLOCK TEACHER UPDATE WITHOUT PERMISSION
    // =========================================
    const showTeacherPermissionDenied = async (action = "update") => {
        playPermissionAudio();

        const isDelete = action === "delete";

        return showFormAlert({
            icon: "error",
            title: "Permission Denied",
            text: isDelete
                ? "I'M SORRY! You cannot delete this grade. An administrator must authorize this action."
                : "I'M SORRY! You cannot update this grade. Admin, VPI, or VPA must enable teacher update permission first. Thank you!",
            confirmButtonText: "Understood",
        });
    };

    // =========================================
    // SUBMIT GRADE
    // =========================================
    const handleSubmit = async (event) => {
        event.preventDefault();

        if (submitting) return;

        if (
            !selectedGradeClass ||
            !selectedStudent ||
            !selectedSubject ||
            !selectedPeriod
        ) {
            await showFormAlert({
                icon: "warning",
                title: "Missing Required Fields",
                text:
                    "Grade Class, Student, Subject, and Period " +
                    "are all required.",
            });

            return;
        }

        // Teacher cannot update a grade without permission.
        if (
            userType === "teacher" &&
            editMode &&
            !allowTeacherUpdate
        ) {
            await showTeacherPermissionDenied("update");
            return;
        }

        const scoreError = validateScores();

        if (scoreError) {
            await showFormAlert({
                icon: "warning",
                title: "Check Assessment Scores",
                text: scoreError,
            });

            return;
        }

        const data = {
            grade_class: selectedGradeClass,
            student: selectedStudent,
            subject: selectedSubject,
            period: selectedPeriod,

            quiz_score: Number(scores.quizScore),
            assignment_score: Number(scores.assignmentScore),
            participation_score: Number(scores.participationScore),
            test_score: Number(scores.testScore),

            scale_grade:
                scores.scaleGrade === "" ||
                scores.scaleGrade === null ||
                scores.scaleGrade === undefined ||
                (
                    typeof scores.scaleGrade === "string" &&
                    scores.scaleGrade.trim() === ""
                )
                    ? null
                    : Number(scores.scaleGrade),

            allow_teacher_update: allowTeacherUpdate,
        };

        try {
            setSubmitting(true);

            await api({
                method: editingId ? "put" : "post",

                url: editingId
                    ? `/api/users/grades-distribution/${editingId}/`
                    : "/api/users/grades-distribution/",

                data,

                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const successMessage = editMode
                ? "Grade updated successfully."
                : "Grade created successfully.";

            setDialogOpen(false);
            setEditGradeId(null);

            await showFormAlert({
                icon: "success",
                title: "Saved Successfully",
                text: successMessage,
                timer: 1800,
                showConfirmButton: false,
            });

            await fetchGradeDistributions(
                page + 1,
                rowsPerPage,
                searchTerm
            );
        } catch (error) {
            await showBackendError(error);
        } finally {
            setSubmitting(false);
        }
    };

    // =========================================
    // DELETE GRADE HANDLER
    //
    // Call this function from your grade table's
    // delete button using the selected grade ID.
    //
    // This component blocks teacher deletion and
    // plays the permission-denied audio.
    // =========================================
    const handleDeleteGrade = async (gradeId) => {
        if (submitting) return;

        if (userType === "teacher") {
            await showTeacherPermissionDenied("delete");
            return;
        }

        if (
            !["admin", "vpi", "vpa"].includes(userType)
        ) {
            await showFormAlert({
                icon: "error",
                title: "Permission Denied",
                text: "You do not have permission to delete this grade.",
            });

            return;
        }

        const confirmation = await Swal.fire({
            icon: "warning",
            title: "Delete Grade Record?",
            text: "This action cannot be undone. Are you sure you want to delete this grade?",
            showCancelButton: true,
            confirmButtonText: "Yes, Delete",
            cancelButtonText: "Cancel",
            buttonsStyling: false,
            customClass: {
                popup: "grade-form-alert",
                title: "grade-form-alert-title",
                htmlContainer: "grade-form-alert-message",
                confirmButton: "grade-form-alert-button",
                cancelButton: "grade-form-alert-cancel-button",
            },
            allowOutsideClick: true,
            allowEscapeKey: true,
        });

        if (!confirmation.isConfirmed) return;

        try {
            setSubmitting(true);

            await api.delete(
                `/api/users/grades-distribution/${gradeId}/`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            await Swal.fire({
                icon: "success",
                title: "Grade Deleted",
                text: "The grade record was deleted successfully.",
                timer: 1800,
                showConfirmButton: false,
                buttonsStyling: false,
                customClass: {
                    popup: "grade-form-alert",
                    title: "grade-form-alert-title",
                    htmlContainer: "grade-form-alert-message",
                },
            });

            await fetchGradeDistributions(
                page + 1,
                rowsPerPage,
                searchTerm
            );
        } catch (error) {
            console.error(
                "Delete Grade Error:",
                error?.response?.data || error
            );

            await Swal.fire({
                icon: "error",
                title: "Unable to Delete Grade",
                text: getBackendErrorMessage(error),
                confirmButtonText: "Understood",
                buttonsStyling: false,
                customClass: {
                    popup: "grade-form-alert",
                    title: "grade-form-alert-title",
                    htmlContainer: "grade-form-alert-message",
                    confirmButton: "grade-form-alert-button",
                },
            });
        } finally {
            setSubmitting(false);
        }
    };

    // =========================================
    // AUTOCOMPLETE VALUE LOOKUP
    // =========================================
    const findOption = (options, value) =>
        (options || []).find(
            (option) =>
                String(option.id) === String(value)
        ) || null;

    // =========================================
    // RENDER
    // =========================================
    return (
        <>
            <Button
                variant="contained"
                startIcon={<SchoolIcon />}
                onClick={() => handleOpenDialog()}
                disabled={submitting}
                sx={{
                    minHeight: 44,
                    px: 2.5,
                    borderRadius: "10px",
                    textTransform: "none",
                    fontSize: "12px",
                    fontWeight: 800,
                    bgcolor: COLORS.gold,
                    color: COLORS.navy,
                    boxShadow: "none",
                    width: {
                        xs: "100%",
                        sm: "auto",
                    },
                    "&:hover": {
                        bgcolor: "#EFBF30",
                        boxShadow:
                            "0 5px 15px rgba(246,201,68,0.25)",
                    },
                    "&.Mui-disabled": {
                        bgcolor: "#E2E8F0",
                        color: COLORS.muted,
                    },
                }}
            >
                Add Grade
            </Button>

            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                fullWidth
                maxWidth="sm"
                scroll="paper"
                aria-labelledby="grade-form-title"
                PaperProps={{
                    sx: {
                        width: "100%",
                        maxWidth: "620px",
                        maxHeight: {
                            xs: "100%",
                            sm: "calc(100% - 48px)",
                        },
                        m: {
                            xs: 0,
                            sm: 2,
                        },
                        borderRadius: {
                            xs: 0,
                            sm: "18px",
                        },
                        overflow: "hidden",
                        backgroundColor: COLORS.background,
                        boxShadow:
                            "0 25px 70px rgba(6,42,75,0.20)",
                    },
                }}
            >
                {/* HEADER */}
                <DialogTitle
                    id="grade-form-title"
                    component="div"
                    sx={{
                        position: "relative",
                        p: {
                            xs: 2,
                            sm: 2.5,
                        },
                        color: COLORS.white,
                        background:
                            `linear-gradient(120deg, ${COLORS.navy}, ${COLORS.deepBlue})`,
                        borderBottom: `4px solid ${COLORS.gold}`,
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                    >
                        <Box
                            sx={{
                                width: 46,
                                height: 46,
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "13px",
                                color: COLORS.navy,
                                bgcolor: COLORS.gold,
                            }}
                        >
                            {editMode ? (
                                <EditOutlined sx={{ fontSize: 27 }} />
                            ) : (
                                <SchoolIcon sx={{ fontSize: 27 }} />
                            )}
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: {
                                        xs: "18px",
                                        sm: "21px",
                                    },
                                    lineHeight: 1.3,
                                    fontWeight: 900,
                                    letterSpacing: "-0.4px",
                                }}
                            >
                                {editMode
                                    ? "Update Grade Record"
                                    : "Create Grade Record"}
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.5,
                                    fontSize: "11px",
                                    lineHeight: 1.6,
                                    color: "rgba(255,255,255,0.76)",
                                }}
                            >
                                {editMode
                                    ? "Review and update the student's assessment details."
                                    : "Enter the student's academic and assessment details."}
                            </Typography>
                        </Box>

                        <Tooltip title="Close form">
                            <span>
                                <IconButton
                                    onClick={handleCloseDialog}
                                    disabled={submitting}
                                    aria-label="Close grade form"
                                    sx={{
                                        alignSelf: "flex-start",
                                        color: COLORS.white,
                                        bgcolor:
                                            "rgba(255,255,255,0.10)",
                                        borderRadius: "9px",
                                        "&:hover": {
                                            bgcolor:
                                                "rgba(255,255,255,0.18)",
                                        },
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>

                    <Stack
                        direction="row"
                        flexWrap="wrap"
                        gap={0.75}
                        sx={{ mt: 2 }}
                    >
                        <Chip
                            size="small"
                            icon={
                                <CalendarMonth
                                    sx={{
                                        color:
                                            `${COLORS.gold} !important`,
                                        fontSize: "15px !important",
                                    }}
                                />
                            }
                            label={
                                selectedYear?.name ||
                                selectedYear?.year ||
                                selectedYear?.academic_year ||
                                "Academic Year"
                            }
                            sx={{
                                maxWidth: "100%",
                                height: 25,
                                color: COLORS.white,
                                bgcolor:
                                    "rgba(255,255,255,0.10)",
                                fontSize: "10px",
                                fontWeight: 750,
                                "& .MuiChip-label": {
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                },
                            }}
                        />

                        <Chip
                            size="small"
                            label={editMode ? "Editing" : "New Record"}
                            sx={{
                                height: 25,
                                color: COLORS.navy,
                                bgcolor: COLORS.gold,
                                fontSize: "10px",
                                fontWeight: 850,
                            }}
                        />
                    </Stack>
                </DialogTitle>

                {/* FORM CONTENT */}
                <DialogContent
                    sx={{
                        p: {
                            xs: 1.75,
                            sm: 2.5,
                        },
                        pt: {
                            xs: 2,
                            sm: 2.5,
                        },
                        overflowY: "auto",
                        "&::-webkit-scrollbar": {
                            width: 6,
                        },
                        "&::-webkit-scrollbar-thumb": {
                            bgcolor: "#CBD5E1",
                            borderRadius: 10,
                        },
                    }}
                >
                    <Box
                        component="form"
                        id="grade-distribution-form"
                        onSubmit={handleSubmit}
                        noValidate
                    >
                        {/* STUDENT INFORMATION */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: {
                                    xs: 1.5,
                                    sm: 2,
                                },
                                borderRadius: "13px",
                                border: `1px solid ${COLORS.border}`,
                                bgcolor: COLORS.white,
                                mb: 2,
                            }}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ mb: 2 }}
                            >
                                <Box
                                    sx={{
                                        width: 33,
                                        height: 33,
                                        borderRadius: "9px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        bgcolor: "#EFF6FF",
                                        color: COLORS.deepBlue,
                                    }}
                                >
                                    <PersonOutline sx={{ fontSize: 20 }} />
                                </Box>

                                <Box>
                                    <Typography
                                        sx={{
                                            color: COLORS.navy,
                                            fontWeight: 850,
                                            fontSize: "13px",
                                        }}
                                    >
                                        Student Information
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: 0.25,
                                            color: COLORS.muted,
                                            fontSize: "10px",
                                        }}
                                    >
                                        Select the class and student receiving
                                        this grade.
                                    </Typography>
                                </Box>
                            </Stack>

                            <Grid container spacing={1.75}>
                                <Grid item xs={12}>
                                    <Autocomplete
                                        fullWidth
                                        options={gradeClasses || []}
                                        value={findOption(
                                            gradeClasses,
                                            selectedGradeClass
                                        )}
                                        disabled={submitting}
                                        getOptionLabel={(option) =>
                                            option?.name || ""
                                        }
                                        isOptionEqualToValue={(
                                            option,
                                            value
                                        ) =>
                                            String(option.id) ===
                                            String(value.id)
                                        }
                                        onChange={(event, value) => {
                                            setSelectedGradeClass(
                                                value?.id ?? ""
                                            );
                                            setSelectedStudent("");
                                            setFilteredStudents([]);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Grade Class"
                                                placeholder="Choose a grade class"
                                                required
                                                sx={fieldSx}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Autocomplete
                                        fullWidth
                                        options={filteredStudents}
                                        loading={loadingStudents}
                                        disabled={
                                            submitting ||
                                            !selectedGradeClass ||
                                            !selectedYear?.id
                                        }
                                        value={findOption(
                                            filteredStudents,
                                            selectedStudent
                                        )}
                                        getOptionLabel={(option) =>
                                            option?.full_name || ""
                                        }
                                        isOptionEqualToValue={(
                                            option,
                                            value
                                        ) =>
                                            String(option.id) ===
                                            String(value.id)
                                        }
                                        onChange={(event, value) => {
                                            setSelectedStudent(
                                                value?.id ?? ""
                                            );
                                        }}
                                        noOptionsText={
                                            !selectedGradeClass
                                                ? "Select a grade class first"
                                                : loadingStudents
                                                    ? "Loading students..."
                                                    : "No students found for this class"
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Student"
                                                placeholder={
                                                    loadingStudents
                                                        ? "Loading registered students..."
                                                        : selectedGradeClass
                                                            ? "Search and select a student"
                                                            : "Select a grade class first"
                                                }
                                                required
                                                sx={fieldSx}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadingStudents && (
                                                                <CircularProgress
                                                                    color="inherit"
                                                                    size={18}
                                                                    sx={{ mr: 1 }}
                                                                />
                                                            )}
                                                            {
                                                                params.InputProps
                                                                    .endAdornment
                                                            }
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        fullWidth
                                        options={subjects || []}
                                        value={findOption(
                                            subjects,
                                            selectedSubject
                                        )}
                                        disabled={submitting}
                                        getOptionLabel={(option) =>
                                            option?.name || ""
                                        }
                                        isOptionEqualToValue={(
                                            option,
                                            value
                                        ) =>
                                            String(option.id) ===
                                            String(value.id)
                                        }
                                        onChange={(event, value) => {
                                            setSelectedSubject(
                                                value?.id ?? ""
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Subject Asign To You"
                                                placeholder="Select Subject"
                                                required
                                                sx={fieldSx}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        fullWidth
                                        options={periods || []}
                                        value={findOption(
                                            periods,
                                            selectedPeriod
                                        )}
                                        disabled={submitting}
                                        getOptionLabel={(option) =>
                                            option?.name || ""
                                        }
                                        isOptionEqualToValue={(
                                            option,
                                            value
                                        ) =>
                                            String(option.id) ===
                                            String(value.id)
                                        }
                                        onChange={(event, value) => {
                                            setSelectedPeriod(
                                                value?.id ?? ""
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Period"
                                                placeholder="Select period"
                                                required
                                                sx={fieldSx}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* ASSESSMENT SCORES */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: {
                                    xs: 1.5,
                                    sm: 2,
                                },
                                borderRadius: "13px",
                                border: `1px solid ${COLORS.border}`,
                                bgcolor: COLORS.white,
                                mb: 2,
                            }}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ mb: 1 }}
                            >
                                <Box
                                    sx={{
                                        width: 33,
                                        height: 33,
                                        borderRadius: "9px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        bgcolor: "#FFF7DB",
                                        color: COLORS.orange,
                                    }}
                                >
                                    <Assessment sx={{ fontSize: 20 }} />
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        sx={{
                                            color: COLORS.navy,
                                            fontWeight: 850,
                                            fontSize: "13px",
                                        }}
                                    >
                                        Assessment Scores
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: 0.25,
                                            color: COLORS.muted,
                                            fontSize: "10px",
                                        }}
                                    >
                                        Complete all required scores.
                                        Scale Grade is optional.
                                    </Typography>
                                </Box>
                            </Stack>

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1,
                                    bgcolor: "#F8FAFC",
                                    border: `1px solid ${COLORS.border}`,
                                    borderRadius: "9px",
                                    p: 1.25,
                                    mb: 2,
                                }}
                            >
                                <InfoOutlined
                                    sx={{
                                        color: COLORS.deepBlue,
                                        fontSize: 18,
                                        mt: 0.1,
                                        flexShrink: 0,
                                    }}
                                />

                                <Typography
                                    sx={{
                                        color: COLORS.muted,
                                        fontSize: "10px",
                                        lineHeight: 1.7,
                                    }}
                                >
                                    Enter <strong>0</strong> if the student
                                    received zero marks. Quiz, Assignment,
                                    Participation, and Test scores are
                                    required.{" "}
                                    <strong>
                                        Scale Grade may be left blank.
                                    </strong>{" "}
                                    Empty required scores are not
                                    automatically converted to zero.
                                </Typography>
                            </Box>

                            <Grid container spacing={1.75}>
                                {SCORE_FIELDS.map((field) => (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        key={field.key}
                                    >
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label={field.label}
                                            value={scores[field.key]}
                                            disabled={submitting}
                                            placeholder={
                                                field.required
                                                    ? "Enter score (e.g. 0)"
                                                    : "Optional — leave blank"
                                            }
                                            onChange={(event) =>
                                                handleScoreChange(
                                                    field.key,
                                                    event.target.value
                                                )
                                            }
                                            required={field.required}
                                            sx={fieldSx}
                                            inputProps={{
                                                min: 0,
                                                step: "any",
                                                inputMode: "decimal",
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Assessment
                                                            sx={{
                                                                color: COLORS.muted,
                                                                fontSize: 17,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            helperText={
                                                field.required
                                                    ? field.description
                                                    : `${field.description} • Not required`
                                            }
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>

                        {/* TEACHER PERMISSION */}
                        {["admin", "vpi", "vpa"].includes(userType) && (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 1.75,
                                    borderRadius: "13px",
                                    border: `1px solid ${COLORS.border}`,
                                    bgcolor: COLORS.white,
                                }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={1.25}
                                    alignItems="center"
                                >
                                    <Box
                                        sx={{
                                            width: 37,
                                            height: 37,
                                            borderRadius: "10px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: allowTeacherUpdate
                                                ? "#DCFCE7"
                                                : "#F1F5F9",
                                            color: allowTeacherUpdate
                                                ? COLORS.success
                                                : COLORS.muted,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {allowTeacherUpdate ? (
                                            <VerifiedOutlined />
                                        ) : (
                                            <LockOutlined />
                                        )}
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                color: COLORS.navy,
                                                fontWeight: 800,
                                                fontSize: "12px",
                                            }}
                                        >
                                            Teacher Editing Permission
                                        </Typography>

                                        <Typography
                                            sx={{
                                                color: COLORS.muted,
                                                fontSize: "10px",
                                                mt: 0.35,
                                                lineHeight: 1.6,
                                            }}
                                        >
                                            {allowTeacherUpdate
                                                ? "Teachers are allowed to update this grade."
                                                : "Teachers cannot update this grade unless permission is enabled."}
                                        </Typography>
                                    </Box>

                                    <Button
                                        size="small"
                                        variant={
                                            allowTeacherUpdate
                                                ? "contained"
                                                : "outlined"
                                        }
                                        disabled={submitting}
                                        onClick={() =>
                                            setAllowTeacherUpdate(
                                                (previous) => !previous
                                            )
                                        }
                                        sx={{
                                            flexShrink: 0,
                                            textTransform: "none",
                                            fontWeight: 800,
                                            fontSize: "10px",
                                            borderRadius: "8px",
                                            minWidth: 82,
                                            color: allowTeacherUpdate
                                                ? COLORS.white
                                                : COLORS.deepBlue,
                                            bgcolor: allowTeacherUpdate
                                                ? COLORS.success
                                                : "transparent",
                                            borderColor: COLORS.border,
                                            "&:hover": {
                                                bgcolor: allowTeacherUpdate
                                                    ? "#166534"
                                                    : "#EFF6FF",
                                            },
                                        }}
                                    >
                                        {allowTeacherUpdate
                                            ? "Enabled"
                                            : "Enable"}
                                    </Button>
                                </Stack>
                            </Paper>
                        )}
                    </Box>
                </DialogContent>

                {/* FORM ACTIONS */}
                <DialogActions
                    sx={{
                        p: {
                            xs: 1.5,
                            sm: 2.5,
                        },
                        px: {
                            xs: 1.75,
                            sm: 2.5,
                        },
                        gap: 1,
                        borderTop: `1px solid ${COLORS.border}`,
                        bgcolor: COLORS.white,
                        flexDirection: {
                            xs: "column-reverse",
                            sm: "row",
                        },
                        "& > :not(style) ~ :not(style)": {
                            ml: {
                                xs: 0,
                                sm: 1,
                            },
                        },
                    }}
                >
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleCloseDialog}
                        disabled={submitting}
                        sx={{
                            minHeight: 44,
                            borderRadius: "10px",
                            px: 2.5,
                            textTransform: "none",
                            fontSize: "12px",
                            fontWeight: 800,
                            color: COLORS.navy,
                            borderColor: COLORS.border,
                            width: {
                                xs: "100%",
                                sm: "auto",
                            },
                            "&:hover": {
                                borderColor: COLORS.deepBlue,
                                bgcolor: "#F8FAFC",
                            },
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        fullWidth
                        type="submit"
                        form="grade-distribution-form"
                        variant="contained"
                        disabled={submitting}
                        startIcon={
                            submitting ? (
                                <CircularProgress
                                    size={17}
                                    color="inherit"
                                />
                            ) : (
                                <SaveOutlined />
                            )
                        }
                        sx={{
                            minHeight: 44,
                            borderRadius: "10px",
                            px: 3,
                            textTransform: "none",
                            fontSize: "12px",
                            fontWeight: 850,
                            color: COLORS.white,
                            bgcolor: COLORS.navy,
                            boxShadow: "none",
                            width: {
                                xs: "100%",
                                sm: "auto",
                            },
                            "&:hover": {
                                bgcolor: COLORS.deepBlue,
                                boxShadow:
                                    "0 5px 15px rgba(6,42,75,0.18)",
                            },
                            "&.Mui-disabled": {
                                bgcolor: "#CBD5E1",
                                color: COLORS.white,
                            },
                        }}
                    >
                        {submitting
                            ? "Saving Grade..."
                            : editMode
                                ? "Update Grade"
                                : "Save Grade"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AddAndUpDateGrades;
